import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Badge from 'react-bootstrap/Badge'
import QuickLookDrawer from 'transactions/drawer'
import ProcessingAuditReportDrawer from 'transactions/processing-audit-report-drawer'
import Button from 'react-bootstrap/Button'
import NewReviewBadge from 'shared/badges/new-review-badge'
import { VendorChangeBadge as VendorChange } from 'shared/badges'
import ViewReceiptModal from 'receipt-scans/ViewReceiptModal'
import { useNavigate } from 'react-router-dom'
import { truncate, isNull } from 'lodash'
import { displayPercent, twoDigitMoney } from 'helpers/utils'
import FlagCount from 'shared/flag-count'
import MultiReviewStatusColumn from './multi-review-status-column'
import ReviewStatusColumn from './review-status-column'
import TestingStatus from './testing-status'
import RiskBadge from 'shared/badges/risk-badge'
import TextCell from './cells/TextCell'
import DateCell from './cells/DateCell'
import MoneyCell from './cells/MoneyCell'
import BooleanCell from './cells/BooleanCell'
import TimeInQueueCell from './cells/TimeInQueueCell'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBook,
  faShoppingCart,
  faCreditCard,
  faFileInvoice,
  faFlag,
  faReceipt,
  faDotCircle,
  faRetweet,
  faExclamation,
  faCheck
} from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import CustomColumnCell from './cells/CustomColumnCell'
import AddToListDropdown from 'transactions/add-to-list-dropdown'

const AccountSegment = ({ name, code }) => {
  if (name === null && code === null) {
    return <div>None</div>
  } else {
    return (
      <>
        <span>{name}</span>
        <span>{code ? ` (${code})` : ''}</span>
      </>
    )
  }
}

function TableRow({
  transaction,
  transactions,
  columns,
  auditReportId,
  dashboardId,
  percentColumns,
  twoDigitMoneyColumns,
  moneyColumns,
  accountSegmentColumns,
  flagCountColumns,
  riskScoreColumns,
  textColumns,
  dateColumns,
  booleanColumns,
  timeInQueueColumns = [],
  analyticsColumns = [],
  customColumns = [],
  customColumnValues = {},
  unifiedColumnOrder = null,
  canEdit = true,
  hasSectionStatusColumn = false
}) {
  const [showQuickLook, setShowQuickLook] = useState(false)
  const [showProcessing, setShowProcessing] = useState(false)
  const [initialFeatureTab, setInitialFeatureTab] = useState(null)
  const [showReceiptModal, setShowReceiptModal] = useState(false)
  const navigate = useNavigate() // TODO: reinstate on vendor and employee cells.

  const isVendorChange = transaction.section_name === 'Vendor Changes'
  const isPcChange = transaction.section_name === 'PCard'
  const isGlChange = transaction.section_name === 'General Ledger'
  const isBudgetChange = transaction.section_name === 'Budget'
  const isPurchasingChange = ['Purchase Orders', 'Requisitions'].includes(transaction.section_name)
  const isApChange = transaction.section_name === 'Accounts Payable'

  const displayKey = (transaction) => {
    if (isPcChange || isGlChange) {
      return truncate(transaction.display_key, { length: 15 })
    } else if (isBudgetChange) {
      return transaction.display_key
    } else if (isPurchasingChange) {
      return isNull(transaction.display_key_two)
        ? truncate(`RQ: ${transaction.display_key}`, { length: 15 })
        : truncate(`PO: ${transaction.display_key_two}`, { length: 15 })
    } else {
      return truncate(transaction.display_key_two, { length: 15 })
    }
  }

  const featureStatusIcon = (statusName) => {
    switch (statusName) {
      case 'In-progress': return <FontAwesomeIcon icon={faRetweet} className="text-warning" title="In Progress" />
      case 'Exception':   return <FontAwesomeIcon icon={faExclamation} className="text-danger" title="Exception" />
      case 'No Exception': return <FontAwesomeIcon icon={faCheck} className="text-success" title="No Exception" />
      default:            return <FontAwesomeIcon icon={faDotCircle} className="text-info" title="Open" />
    }
  }

  const renderRegularColumn = (columnName) => {
    if (columnName.startsWith('feature_review:')) {
      const featureKey = columnName.replace('feature_review:', '')
      const featureReview = transaction.feature_reviews?.find(fr => String(fr.feature_key) === featureKey)
      return (
        <td
          key={columnName}
          className="text-center align-middle"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setInitialFeatureTab(featureKey)
            setShowQuickLook(true)
          }}
        >
          {featureStatusIcon(featureReview?.status_name || 'Open')}
        </td>
      )
    } else if (columnName === 'cleared') {
      return (
        <td key={columnName} className="align-middle">
          {isVendorChange ? (
            <VendorChange />
          ) : isGlChange ? (
            <Badge pill className="bg-light text-primary mx-1" variant="light">
              <FontAwesomeIcon icon={faBook} className="text-primary" />{' '}
              {transaction.display_key}-{transaction.display_key_two}
            </Badge>
          ) : isPcChange ? (
            <Badge pill className="bg-light text-primary mx-1" variant="light">
              <FontAwesomeIcon icon={faCreditCard} className="text-primary" />{' '}
              {transaction.display_key}
            </Badge>
          ) : isPurchasingChange ? (
            <Badge pill className="bg-light text-primary mx-1" variant="light">
              <FontAwesomeIcon icon={faShoppingCart} className="text-primary" />{' '}
              {transaction.display_key}
            </Badge>
          ) : isApChange ? (
            <Badge pill className="bg-light text-primary mx-1" variant="light">
              <FontAwesomeIcon icon={faFileInvoice} className="text-primary" />{' '}
              {transaction.display_key_two}
            </Badge>
          ) : null}
        </td>
      )
    } else if (columnName === 'id') {
      const hasReceipt = transaction.transaction_details?.receipt_scan != null
      return (
        <td key={columnName} className="align-middle">
          <Button variant="link" onClick={() => setShowQuickLook(true)}>
            {isPcChange
              ? transaction?.external_id
              : transaction.section_display_key + ' ' + transaction.display_key}
          </Button>
          {hasReceipt && (
            <FontAwesomeIcon
              icon={faReceipt}
              className="text-success ms-1"
              title="Receipt attached - click to view"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation()
                setShowReceiptModal(true)
              }}
            />
          )}
        </td>
      )
    } else if (columnName === 'view') {
      return (
        <td key={columnName} className="align-middle">
          <Button variant="link" onClick={() => setShowQuickLook(true)}>
            {displayKey(transaction)}
          </Button>
        </td>
      )
    } else if (columnName === 'processing_modal') {
      return (
        <td key={columnName} className="align-middle">
          <Button
            variant="link"
            onClick={() => auditReportId ? setShowProcessing(true) : setShowQuickLook(true)}
          >
            {transaction.external_id}
          </Button>
          {transaction?.is_viewed === false && (
            <NewReviewBadge showCount={false} />
          )}
        </td>
      )
    } else if (columnName === 'report_analytics_count') {
      return (
        <td key={'report_analytics_count'} className="text-center align-middle">
          <FlagCount
            count={
              transaction['report_analytics_count'] !== undefined
                ? transaction['report_analytics_count']
                : transaction.transaction_details?.['report_analytics_count']
            }
          />
        </td>
      )
    } else if (columnName === 'vendor') {
      return (
        <td key={columnName} className="align-middle text-muted fw-light">
          <a
            href={`/vendors/${transaction.vendor_id}`}
            onClick={(e) => {
              e.preventDefault()
              navigate(`/vendors/${transaction.vendor_id}`)
            }}
          >
            {truncate(transaction.vendor_name, { length: 20 })}
          </a>
        </td>
      )
    } else if (columnName === 'employee') {
      return (
        <td key={columnName} className="align-middle text-muted fw-light">
          <a
            href={`/employees/${transaction.employee_id}`}
            onClick={(e) => {
              e.preventDefault()
              navigate(`/employees/${transaction.employee_id}`)
            }}
          >
            {transaction.employee_external_id}
          </a>
        </td>
      )
    } else if (columnName === 'single_status') {
      return (
        <td key={'single_status'} className="align-middle text-center">
          <ReviewStatusColumn transaction={transaction} />
        </td>
      )
    } else if (columnName === 'testing_status') {
      return (
        <td key={'testing_status'} className="align-middle text-center">
          <TestingStatus featureSummary={transaction.feature_summary} />
        </td>
      )
    } else if (columnName === 'status' && !hasSectionStatusColumn) {
      return (
        <td key={'status'} className="align-middle text-center">
          <MultiReviewStatusColumn reviewSummary={transaction.review_summary} />
        </td>
      )
    } else if (columnName === 'employee_full_name') {
      return (
        <TextCell
          value={
            transaction['employee_full_name'] !== undefined
              ? transaction['employee_full_name']
              : transaction.transaction_details?.['employee_full_name']
          }
          key={'employee_full_name'}
        />
      )
    } else if (columnName === 'transaction_on') {
      return (
        <td key={'transaction_on'} className="align-middle text-center">
          {moment(transaction.transaction_on).format('MM/DD/YYYY')}
        </td>
        )
    } else if (columnName === 'line_amount') {
      return (
        <td key={'line_amount'} className="align-middle text-center">
          {twoDigitMoney(transaction.line_amount)}
        </td>
      )
    } else if (columnName === 'vendor_id') {
      return <td key={columnName}>{transaction.vendor_external_id}</td>
    } else if (columnName === 'add_to_list') {
      return (
        <td key={columnName} className="align-middle text-center" style={{ maxWidth: '75px', width: '75px' }}>
          <AddToListDropdown transaction={transaction} />
        </td>
      )
    } else {
      // Fall back to the section detail when the top-level value is null, not just absent.
      const value = transaction[columnName] ?? transaction.transaction_details?.[columnName]

      if (riskScoreColumns.includes(columnName)) {
        return (
          <td key={columnName} className="text-center align-middle">
            <RiskBadge count={value} showCount={true} />
          </td>
        )
      }

      if (flagCountColumns.includes(columnName)) {
        return (
          <td key={columnName} className="text-center align-middle">
            <FlagCount count={value} />
          </td>
        )
      }

      if (percentColumns.includes(columnName)) {
        return <td key={columnName}>{displayPercent(value)}</td>
      }

      if (twoDigitMoneyColumns.includes(columnName)) {
        return <td key={columnName}>{twoDigitMoney(value)}</td>
      }

      if (moneyColumns.includes(columnName)) {
        return (
          <td key={columnName}>
            <MoneyCell value={value} />
          </td>
        )
      }

      if (textColumns.includes(columnName)) {
        return <TextCell value={value} key={columnName} />
      }

      if (accountSegmentColumns.includes(columnName)) {
        const nameKey = `${columnName}_name`
        const codeKey = `${columnName}_code`
        return (
          <td key={columnName} className="align-middle text-muted fw-light">
            <AccountSegment
              name={transaction.transaction_details?.[nameKey]}
              code={transaction.transaction_details?.[codeKey]}
            />
          </td>
        )
      }

      if (dateColumns.includes(columnName)) {
        return <DateCell value={value} key={columnName} />
      }

      if (booleanColumns.includes(columnName)) {
        return <BooleanCell value={value} key={columnName} />
      }

      const timeInQueueCol = timeInQueueColumns.find(
        (c) => c.database_column_name === columnName
      )
      if (timeInQueueCol) {
        return (
          <TimeInQueueCell
            value={value}
            accessorKey={timeInQueueCol.accessor_key}
            key={columnName}
          />
        )
      }

      return <td key={columnName}>{value}</td>
    }
  }

  const renderColumn = (columnData, type) => {
    if (type === 'custom') {
      const customColumnValue = customColumnValues[columnData.id]
      return (
        <CustomColumnCell
          key={`custom-${columnData.id}`}
          customColumn={columnData}
          transactionId={transaction.id}
          dashboardId={dashboardId}
          initialValue={customColumnValue?.value}
          customColumnValueId={customColumnValue?.id}
          canEdit={canEdit}
        />
      )
    } else if (type === 'analytics') {
      const hasFlag = transaction.transaction_analytics?.some(ta =>
        ta.analytic_id === columnData.id && !ta.is_resolved
      )
      return (
        <td key={`analytics-${columnData.id}`} className="text-center align-middle">
          {hasFlag && (
            <FontAwesomeIcon icon={faFlag} className="text-danger" />
          )}
        </td>
      )
    } else {
      return renderRegularColumn(columnData)
    }
  }

  return (
    <tr className={isVendorChange ? 'table-light' : ''}>
      {/* Use unified column order if available, otherwise fallback to separate rendering */}
      {unifiedColumnOrder && unifiedColumnOrder.length > 0 ? (
        unifiedColumnOrder.map((item) => renderColumn(item.data, item.type))
      ) : (
        <>
          {columns.map((columnName) => renderRegularColumn(columnName))}
          {customColumns.map((customColumn) => {
            const customColumnValue = customColumnValues[customColumn.id]
            return (
              <CustomColumnCell
                key={`custom-${customColumn.id}`}
                customColumn={customColumn}
                transactionId={transaction.id}
                dashboardId={dashboardId}
                initialValue={customColumnValue?.value}
                customColumnValueId={customColumnValue?.id}
                canEdit={canEdit}
              />
            )
          })}
          {/* Analytics columns are rendered at the end when no unified order is available */}
          {analyticsColumns.map((analyticsColumn) => {
            const hasFlag = transaction.transaction_analytics?.some(ta =>
              ta.analytic_id === analyticsColumn.id && !ta.is_resolved
            )
            return (
              <td key={`analytics-${analyticsColumn.id}`} className="text-center align-middle">
                {hasFlag && (
                  <FontAwesomeIcon icon={faFlag} className="text-danger" />
                )}
              </td>
            )
          })}
        </>
      )}

      {showQuickLook && (
        <QuickLookDrawer
          transactions={transactions}
          transactionId={transaction.id}
          show={showQuickLook}
          setShow={(v) => {
            setShowQuickLook(v)
            if (!v) setInitialFeatureTab(null)
          }}
          dashboardId={dashboardId}
          canEdit={canEdit}
          auditReportId={auditReportId}
          initialFeatureTab={initialFeatureTab}
        />
      )}
      {showProcessing && auditReportId && (
        <ProcessingAuditReportDrawer
          transactions={transactions}
          transactionId={transaction.id}
          externalId={transaction.external_id}
          auditReportId={auditReportId}
          show={showProcessing}
          setShow={setShowProcessing}
        />
      )}
      {showReceiptModal && transaction.transaction_details?.receipt_scan && (
        <ViewReceiptModal
          show={showReceiptModal}
          onHide={() => setShowReceiptModal(false)}
          receiptScan={transaction.transaction_details.receipt_scan}
        />
      )}
    </tr>
  )
}

TableRow.propTypes = {
  transaction: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired
}

export default TableRow
